import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Card, Tag, Input, Select, Row, Col, Statistic } from 'antd';
import { PlusOutlined, SearchOutlined, BankOutlined, TeamOutlined, DollarOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { trpc } from '../../../lib/api';
import { adminPath } from '../../../lib/adminPaths';
import { CompanyModal } from './CompanyModal';
import { CompanyBranchesModal } from './CompanyBranchesModal';
import { NameLink, ListActionsDropdown } from '../../../components/common';
import { ResponsiveTable, type ResponsiveColumnType } from '../../../components/common/responsive';
import type { MenuProps } from 'antd';
import type { Company, CompanyContractDestination, CompanyContractPaymentProvider } from '../../../types/company';

const { Search } = Input;
const { Option } = Select;
type CompanyListItem = Company;
type CompanySubscriptionStatusFilter =
  'trialing' |
  'pending_payment' |
  'active' |
  'past_due' |
  'canceled' |
  'suspended' |
  undefined;

const serviceTypePresentations: Record<string, { label: string; color: string }> = {
  fc: { label: '工場', color: 'blue' },
  bm: { label: 'ビルメンテ', color: 'green' },
  ht: { label: 'ホテル', color: 'purple' },
};

const contractPaymentProviderPresentations: Record<CompanyContractPaymentProvider, { label: string; color: string }> = {
  stripe: { label: 'カード', color: 'blue' },
  manual: { label: '手動', color: 'orange' },
  bank_transfer: { label: '銀行振込', color: 'cyan' },
};

const getServiceTypePresentation = (clientType?: string | null): { label: string; color: string } => {
  const normalizedClientType = clientType?.trim() || 'fc';
  return serviceTypePresentations[normalizedClientType] ?? {
    label: `未定義: ${normalizedClientType}`,
    color: 'default',
  };
};

const getContractDestination = (company: CompanyListItem): CompanyContractDestination => {
  if (company.contractDestination) {
    return company.contractDestination;
  }

  if (company.agencyUserId || company.agencyCouponCode) {
    return {
      kind: 'agency',
      name: '代理店情報未登録',
      agencyUserId: company.agencyUserId ?? null,
      agencyCouponCode: company.agencyCouponCode ?? null,
    };
  }

  return {
    kind: 'direct',
    name: '直契約',
    agencyUserId: null,
    agencyCouponCode: null,
  };
};

export const CompanyListPage = () => {
  const [selectedCompany, setSelectedCompany] = useState<CompanyListItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBranchesModalOpen, setIsBranchesModalOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState<CompanySubscriptionStatusFilter>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const navigate = useNavigate();

  // 会社一覧取得
  const { data: companiesData, isLoading, refetch } = trpc.company.list.useQuery({
    page: currentPage,
    limit: pageSize,
    search: searchText || undefined,
    subscriptionStatus,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  // 統計情報取得
  const { data: stats } = trpc.company.getStats.useQuery();

  const handleEdit = (company: CompanyListItem) => {
    setSelectedCompany(company);
    setIsModalOpen(true);
  };

  const handleBranches = (company: CompanyListItem) => {
    setSelectedCompany(company);
    setIsBranchesModalOpen(true);
  };

  const columns: ResponsiveColumnType<CompanyListItem>[] = [
    {
      title: '会社名',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <NameLink
            name={text}
            onClick={() => navigate(adminPath(`companies/${record.id}`))}
          />
          {record.nameKana && <div style={{ fontSize: '12px', color: '#999' }}>{record.nameKana}</div>}
        </div>
      ),
      mobileRole: 'title',
      mobilePriority: 1,
    },
    {
      title: 'サービス',
      key: 'clientType',
      width: 120,
      render: (_, record) => {
        const serviceType = getServiceTypePresentation(record.clientType);
        return <Tag color={serviceType.color}>{serviceType.label}</Tag>;
      },
      mobileRole: 'tag',
      mobilePriority: 2,
    },
    {
      title: '契約先',
      key: 'contractDestination',
      width: 180,
      render: (_, record) => {
        const contractDestination = getContractDestination(record);
        return (
          <Space direction="vertical" size={2}>
            <Tag color={contractDestination.kind === 'agency' ? 'geekblue' : 'default'}>
              {contractDestination.name}
            </Tag>
            {contractDestination.agencyCouponCode && (
              <span style={{ fontSize: '12px', color: '#666' }}>
                {contractDestination.agencyCouponCode}
              </span>
            )}
          </Space>
        );
      },
      mobileRole: 'description',
      mobilePriority: 3,
    },
    {
      title: '連絡先',
      key: 'contact',
      render: (_, record) => (
        <div>
          {record.email && <div style={{ fontSize: '12px' }}>{record.email}</div>}
          {record.phone && <div style={{ fontSize: '12px' }}>{record.phone}</div>}
        </div>
      ),
      mobileRole: 'description',
      mobilePriority: 4,
    },
    {
      title: 'プラン',
      dataIndex: 'subscriptionPlan',
      key: 'subscriptionPlan',
      width: 140,
      render: (plan, record) => {
        const colors: Record<string, string> = {
          free: 'default',
          starter: 'blue',
          standard: 'green',
          professional: 'cyan',
          premium: 'gold',
          enterprise: 'purple',
          enterprise_premium: 'magenta',
        };
        const paymentProvider = record.contractPaymentProvider
          ? contractPaymentProviderPresentations[record.contractPaymentProvider]
          : null;
        return (
          <Space size={4} wrap>
            <Tag color={colors[plan] || 'default'}>{plan?.toUpperCase() || '-'}</Tag>
            {paymentProvider && <Tag color={paymentProvider.color}>{paymentProvider.label}</Tag>}
          </Space>
        );
      },
      mobileRole: 'tag',
      mobilePriority: 5,
    },
    {
      title: 'ステータス',
      dataIndex: 'subscriptionStatus',
      key: 'subscriptionStatus',
      width: 100,
      render: (status) => {
        const colors: Record<string, string> = {
          trialing: 'processing',
          active: 'success',
          past_due: 'warning',
          canceled: 'error',
          suspended: 'default'
        };
        const labels: Record<string, string> = {
          trialing: '無料登録',
          active: 'アクティブ',
          past_due: '支払遅延',
          canceled: 'キャンセル済',
          suspended: '停止中'
        };
        return <Tag color={colors[status] || 'default'}>{labels[status] || status}</Tag>;
      },
      mobileRole: 'tag',
      mobilePriority: 6,
    },
    {
      title: '従業員数',
      dataIndex: 'employeeCount',
      key: 'employeeCount',
      width: 100,
      align: 'right',
      render: (count) => count ? `${count}名` : '-',
      mobileRole: 'hidden',
    },
    {
      title: '状態',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 80,
      render: (isActive) => (
        <Tag color={isActive ? 'success' : 'error'}>
          {isActive ? '有効' : '無効'}
        </Tag>
      ),
      mobileRole: 'hidden',
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      align: 'center',
      render: (_, record) => {
        const additionalActions: MenuProps['items'] = [
          {
            key: 'branches',
            icon: <BankOutlined />,
            label: '支店管理',
            onClick: () => handleBranches(record),
          },
        ];

        return (
          <ListActionsDropdown
            onDetail={() => navigate(adminPath(`companies/${record.id}`))}
            onEdit={() => handleEdit(record)}
            additionalActions={additionalActions}
          />
        );
      },
      mobileRole: 'hidden',
    }
  ];

  return (
    <div>
      <h1>会社管理</h1>

      {/* 統計情報 */}
      {stats && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="総会社数"
                value={stats.total}
                prefix={<BankOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="アクティブ"
                value={stats.active}
                prefix={<TeamOutlined />}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="有料プラン"
                value={stats.paying}
                prefix={<DollarOutlined />}
                valueStyle={{ color: '#cf1322' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="無料登録中"
                value={stats.trialing}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      <Card>
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Search
              placeholder="会社名、会社かな、会社メール、管理ユーザー名・メールで検索"
              allowClear
              enterButton={<SearchOutlined />}
              style={{ width: 300 }}
              onSearch={(value) => {
                setSearchText(value);
                setCurrentPage(1);
              }}
            />
            <Select
              placeholder="ステータス"
              style={{ width: 150 }}
              allowClear
              value={subscriptionStatus}
              onChange={(value) => {
                setSubscriptionStatus(value);
                setCurrentPage(1);
              }}
            >
              <Option value="trialing">無料登録</Option>
              <Option value="active">アクティブ</Option>
              <Option value="past_due">支払遅延</Option>
              <Option value="canceled">キャンセル済</Option>
              <Option value="suspended">停止中</Option>
            </Select>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setSelectedCompany(null);
                setIsModalOpen(true);
              }}
            >
              新規会社登録
            </Button>
          </Space>
        </div>

        <ResponsiveTable
          columns={columns}
          dataSource={(companiesData?.data ?? []) as CompanyListItem[]}
          loading={isLoading}
          rowKey="id"
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: companiesData?.pagination.total || 0,
            onChange: (page, size) => {
              setCurrentPage(page);
              if (size) setPageSize(size);
            },
            showSizeChanger: true,
            showTotal: (total) => `全 ${total} 件`
          }}
          mobileCardConfig={{
            titleField: 'name',
            onClick: (record) => navigate(adminPath(`companies/${record.id}`)),
            actions: (record) => (
              <ListActionsDropdown
                onDetail={() => navigate(adminPath(`companies/${record.id}`))}
                onEdit={() => handleEdit(record)}
                additionalActions={[
                  {
                    key: 'branches',
                    icon: <BankOutlined />,
                    label: '支店管理',
                    onClick: () => handleBranches(record),
                  },
                ]}
              />
            ),
          }}
        />
      </Card>

      {/* 会社編集モーダル */}
      <CompanyModal
        open={isModalOpen}
        company={selectedCompany}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedCompany(null);
        }}
        onSuccess={() => {
          setIsModalOpen(false);
          setSelectedCompany(null);
          refetch();
        }}
      />

      {/* 支店管理モーダル */}
      {selectedCompany && (
        <CompanyBranchesModal
          open={isBranchesModalOpen}
          company={selectedCompany}
          onClose={() => {
            setIsBranchesModalOpen(false);
            setSelectedCompany(null);
          }}
        />
      )}
    </div>
  );
};
